import { supabase } from "@/integrations/supabase/client";

export type InterpretationQuality = "clean" | "repaired_minor" | "repaired_major" | null;
export type TimeWindow = "7d" | "30d" | "90d";

export interface InterpretationQualityMetrics {
  totalEvaluated: number;
  clean: { count: number; percentage: number };
  repairedMinor: { count: number; percentage: number };
  repairedMajor: { count: number; percentage: number };
  excluded: number;
  distribution: Array<{
    date: string;
    clean: number;
    repairedMinor: number;
    repairedMajor: number;
    excluded: number;
  }>;
}

export interface RunRecord {
  id: string;
  created_at: string;
  interpretation_quality: InterpretationQuality;
  repair_count: number;
  repair_types: string[];
  final_status: "succeeded" | "failed" | null;
  phase_2_repairs: Array<{
    flaw_id: string;
    original_phase: string;
    repaired_phase: string;
    rule_applied: "merge" | "phase_presence" | "precedence_fallback";
  }>;
}

/**
 * Get interpretation quality metrics aggregated over a time window
 */
export async function getInterpretationQualityMetrics(
  timeWindow: TimeWindow = "30d"
): Promise<InterpretationQualityMetrics> {
  const days = timeWindow === "7d" ? 7 : timeWindow === "30d" ? 30 : 90;
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);
  const startDateStr = startDate.toISOString();

  // Query swing_runs with analysis_data_v3
  const { data, error } = await supabase
    .from("swing_runs")
    .select("id, created_at, analysis_data_v3")
    .gte("created_at", startDateStr)
    .not("analysis_data_v3", "is", null);

  if (error) {
    console.error("Error fetching interpretation quality metrics:", error);
    throw error;
  }

  if (!data || data.length === 0) {
    return {
      totalEvaluated: 0,
      clean: { count: 0, percentage: 0 },
      repairedMinor: { count: 0, percentage: 0 },
      repairedMajor: { count: 0, percentage: 0 },
      excluded: 0,
      distribution: [],
    };
  }

  // Process runs to extract interpretation quality
  const runs: Array<{
    date: string;
    interpretation_quality: InterpretationQuality;
    phase1Completed: boolean;
    phase2ValidationPassed: boolean;
  }> = [];

  for (const run of data) {
    const audit = run.analysis_data_v3?._audit;
    if (!audit) continue;

    const phase1Completed = audit.phase_1_detection !== null;
    const phase2ValidationPassed = audit.phase_2_validation?.after_repair?.valid === true;
    const interpretationQuality = audit.interpretation_quality as InterpretationQuality;

    // Excluded runs: interpretation_quality is null AND Phase-1 completed AND Phase-2 validation passed
    if (
      interpretationQuality === null &&
      phase1Completed &&
      phase2ValidationPassed
    ) {
      runs.push({
        date: run.created_at,
        interpretation_quality: null,
        phase1Completed: true,
        phase2ValidationPassed: true,
      });
      continue;
    }

    // Only count runs where Phase-1 completed and Phase-2 validation passed
    if (phase1Completed && phase2ValidationPassed && interpretationQuality !== null) {
      runs.push({
        date: run.created_at,
        interpretation_quality: interpretationQuality,
        phase1Completed: true,
        phase2ValidationPassed: true,
      });
    }
  }

  // Calculate metrics
  const evaluatedRuns = runs.filter(
    (r) => r.interpretation_quality !== null
  );
  const excludedRuns = runs.filter((r) => r.interpretation_quality === null);

  const clean = evaluatedRuns.filter((r) => r.interpretation_quality === "clean");
  const repairedMinor = evaluatedRuns.filter(
    (r) => r.interpretation_quality === "repaired_minor"
  );
  const repairedMajor = evaluatedRuns.filter(
    (r) => r.interpretation_quality === "repaired_major"
  );

  const totalEvaluated = evaluatedRuns.length;
  const cleanPercentage =
    totalEvaluated > 0 ? (clean.length / totalEvaluated) * 100 : 0;
  const repairedMinorPercentage =
    totalEvaluated > 0 ? (repairedMinor.length / totalEvaluated) * 100 : 0;
  const repairedMajorPercentage =
    totalEvaluated > 0 ? (repairedMajor.length / totalEvaluated) * 100 : 0;

  // Build distribution by date bucket
  const distributionMap = new Map<string, {
    clean: number;
    repairedMinor: number;
    repairedMajor: number;
    excluded: number;
  }>();

  for (const run of runs) {
    const date = new Date(run.date);
    const dateKey = date.toISOString().split("T")[0]; // YYYY-MM-DD

    if (!distributionMap.has(dateKey)) {
      distributionMap.set(dateKey, {
        clean: 0,
        repairedMinor: 0,
        repairedMajor: 0,
        excluded: 0,
      });
    }

    const bucket = distributionMap.get(dateKey)!;
    if (run.interpretation_quality === null) {
      bucket.excluded++;
    } else if (run.interpretation_quality === "clean") {
      bucket.clean++;
    } else if (run.interpretation_quality === "repaired_minor") {
      bucket.repairedMinor++;
    } else if (run.interpretation_quality === "repaired_major") {
      bucket.repairedMajor++;
    }
  }

  // Convert map to array and sort by date
  const distribution = Array.from(distributionMap.entries())
    .map(([date, counts]) => ({
      date,
      ...counts,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));

  return {
    totalEvaluated,
    clean: { count: clean.length, percentage: cleanPercentage },
    repairedMinor: {
      count: repairedMinor.length,
      percentage: repairedMinorPercentage,
    },
    repairedMajor: {
      count: repairedMajor.length,
      percentage: repairedMajorPercentage,
    },
    excluded: excludedRuns.length,
    distribution,
  };
}

/**
 * Get individual run records for the table view
 */
export async function getInterpretationQualityRuns(filters?: {
  interpretation_quality?: InterpretationQuality;
  dateFrom?: string;
  dateTo?: string;
  repairedMajorOnly?: boolean;
}): Promise<RunRecord[]> {
  let query = supabase
    .from("swing_runs")
    .select("id, created_at, analysis_data_v3")
    .not("analysis_data_v3", "is", null)
    .order("created_at", { ascending: false })
    .limit(1000); // Reasonable limit for table view

  if (filters?.dateFrom) {
    query = query.gte("created_at", filters.dateFrom);
  }

  if (filters?.dateTo) {
    query = query.lte("created_at", filters.dateTo);
  }

  const { data, error } = await query;

  if (error) {
    console.error("Error fetching interpretation quality runs:", error);
    throw error;
  }

  if (!data || data.length === 0) {
    return [];
  }

  // Process and filter runs
  const records: RunRecord[] = [];

  for (const run of data) {
    const audit = run.analysis_data_v3?._audit;
    if (!audit) continue;

    const phase1Completed = audit.phase_1_detection !== null;
    const phase2ValidationPassed = audit.phase_2_validation?.after_repair?.valid === true;
    const interpretationQuality = audit.interpretation_quality as InterpretationQuality;

    // Skip excluded runs (null quality with Phase-1 and Phase-2 complete)
    if (
      interpretationQuality === null &&
      phase1Completed &&
      phase2ValidationPassed
    ) {
      continue; // Excluded runs are tracked separately, not shown in table
    }

    // Only include evaluated runs
    if (!phase1Completed || !phase2ValidationPassed || interpretationQuality === null) {
      continue;
    }

    // Apply filters
    if (filters?.interpretation_quality && interpretationQuality !== filters.interpretation_quality) {
      continue;
    }

    if (filters?.repairedMajorOnly && interpretationQuality !== "repaired_major") {
      continue;
    }

    const repairs = audit.phase_2_phase_repair || [];
    const repairTypes = Array.from(
      new Set(repairs.map((r: any) => r.rule_applied))
    ) as string[];

    records.push({
      id: run.id,
      created_at: run.created_at,
      interpretation_quality: interpretationQuality,
      repair_count: repairs.length,
      repair_types: repairTypes,
      final_status: audit.final_outcome?.analysis_status || null,
      phase_2_repairs: repairs,
    });
  }

  return records;
}


